import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Sparkles, Heart, MessageCircle, User, PenLine, Crown, LayoutDashboard } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const links = [
  { to: "/mon-compte", label: "Mon compte", icon: LayoutDashboard, testid: "nav-account-link" },
  { to: "/discover", label: "Découvrir", icon: Sparkles, testid: "nav-discover-link" },
  { to: "/matches", label: "Mes Matchs", icon: MessageCircle, testid: "nav-matches-link", badge: true },
  { to: "/avis", label: "Vos avis", icon: PenLine, testid: "nav-avis-link" },
  { to: "/profile", label: "Mon Profil", icon: User, testid: "nav-profile-link" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let on = true;
    const load = () => api.get("/matches-unread").then((r) => on && setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  const navLinks = user?.role === "admin"
    ? [...links, { to: "/admin", label: "Admin", icon: Crown, testid: "nav-admin-link" }]
    : links;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-terra/15 bg-cream/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-8">
        <NavLink to="/discover" data-testid="nav-home-link">
          <Logo size={36} hideWordmarkOnMobile />
        </NavLink>
        <nav className="flex items-center gap-1 sm:gap-2">
          {navLinks.map(({ to, label, icon: Icon, testid, badge, highlight }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `relative flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-all duration-300 sm:px-4 ${
                  highlight
                    ? "border border-gold/60 bg-gold-light font-semibold text-ink shadow-boho hover:-translate-y-0.5 hover:shadow-card"
                    : isActive
                      ? "bg-terra text-cream"
                      : "text-clay hover:bg-parch hover:text-ink"
                }`
              }
            >
              <Icon size={16} />
              <span className="hidden lg:inline">{label}</span>
              {badge && unread > 0 && (
                <span
                  className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-cream bg-gold px-1 text-[9px] font-bold text-ink"
                  data-testid="nav-unread-badge"
                >
                  {unread}
                </span>
              )}
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            data-testid="nav-logout-button"
            title="Se déconnecter"
            className="ml-1 flex items-center gap-2 rounded-full border border-terra/25 px-3 py-2 text-sm font-medium text-terra transition-all duration-300 hover:bg-terra hover:text-cream"
          >
            <LogOut size={16} />
            <span className="hidden lg:inline">Quitter</span>
          </button>
        </nav>
      </div>
    </header>
  );
};

export const MatchBadge = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold-light px-3 py-1 text-xs font-semibold text-ink">
    <Heart size={12} className="text-terra" fill="#C86D51" />
    {children}
  </span>
);
